// loads scripts that need to be on page - every entrypoint should import this!
import { AdLoader, lazyload } from '@datagraphics/cookbook';

// Import packages
import { map } from 'd3-collection';
import { selSystem, selCounty } from './store.js';
import { get } from 'svelte/store';

// Utils
import { loadJsonScript } from './utils/load-json-script';
import { dateParser, dateFormat } from './utils/d3-utils';

import { renderSvelteComponent } from './utils/render.js';

// Charts
import Select from './components/Select.svelte';
import BarTimeseries from './components/BarTimeseries.svelte';

import Stripes from './components/Stripes.svelte';

import HeatMap from './components/HeatMap.svelte';

import UsMap from './components/UsMap.svelte';

// Colors
import { CHARTS_LIGHT_BLUE, CHARTS_DARK_BLUE } from './colors';

// Utils
import { lazyScript } from './utils/lazy-script';

const lazyLoader = lazyScript();

// mapbox

async function main() {
  // URBAN WATER USE BAR CHART

  const usageCrosswalk = loadJsonScript('usage-crosswalk');

  const usage = loadJsonScript('usage-by-district');

  const statewideUsage = loadJsonScript('usage-state');

  const statewideUsageData = statewideUsage.map((d) => {
    d.reporting_month = dateParser(d.reporting_month);
    d.r_gpcd = d.state_pop_weighted_rgpcd;
    d.pwsid = 'CA0';
    d.supplier_name = 'Statewide';
    d.county = 'Statewide';
    d.average = d.state_pop_weighted_rgpcd;
    return d;
  });

  // console.log(statewideUsageData)

  const usageData = usage.map((d) => {
    d.reporting_month = dateParser(d.reporting_month);
    d.county = d.main_county;
    d.average = d.county_r_gpcd;
    return d;
  });

  const usageDataConcat = statewideUsageData.slice(-12).concat(usageData);

  const usageCrosswalkConcat = usageCrosswalk.concat({
    main_county: 'Statewide',
    supplier_name: 'Statewide',
    pwsid: 'CA0',
  });

  // largest suppliers buttons
  const largestSupplierButtons =
    document.getElementsByName('largest-suppliers');

  largestSupplierButtons.forEach(function (i) {
    i.addEventListener('click', function () {
      //clear any selections in the dropdowns
      selCounty.set(null);
      countySelector.value = '';
      systemSelector.value = '';
      //sets system store value based on button data-id attr
      selSystem.set(i.dataset.id);
    });
  });

  renderSvelteComponent(Select, document.getElementById('select'), {
    data: usageCrosswalkConcat.map(({ main_county, supplier_name, pwsid }) => ({
      main_county,
      supplier_name,
      pwsid,
    })),
    counties: map(usageCrosswalkConcat, function (d) {
      return d.main_county;
    }).keys(),
  });

  // Change the selected system
  const systemSelector = document.getElementById('system-selector');

  systemSelector.onchange = (event) => {
    event.preventDefault();
    selSystem.set(systemSelector.value);
  };

  // Change the selected county
  const countySelector = document.getElementById('county-selector');

  countySelector.onchange = (event) => {
    event.preventDefault();

    selCounty.set(countySelector.value);
    systemSelector.classList.remove('disabled');
    systemSelector.selectedIndex = 0;
  };

  const usageChartContainer = document.getElementById(
    'residential-usage-chart'
  );

  new BarTimeseries({
    target: usageChartContainer,
    props: {
      title: 'Residential water use',
      subhead: 'Statewide residential water use (gallons per person per day)',
      data: usageDataConcat,
      id: 'CA0',
      width: usageChartContainer.offsetWidth,
      margin: { top: 20, right: 10, bottom: 25, left: 25 },
      includeAverage: false,
      attrs: [
        {
          headline: 'Residential water use',
          supplier_name: 'supplier_name',
          hydrologic_region: 'hydrologic_region',
          county: 'county',
          value: 'r_gpcd',
          line_value: 'average',
          barColor: CHARTS_LIGHT_BLUE,
          lineColor: CHARTS_DARK_BLUE,
        },
      ],
      sourceline:
        '<a class="sourceline" href="https://www.waterboards.ca.gov/water_issues/programs/conservation_portal/conservation_reporting.html">California State Water Resources Control Board</a>',
    },
  });

  // STRIPES

  const stripesData = loadJsonScript('stripes-data');

  stripesData.map((d) => {
    d.date = dateParser(d.date);
    return d;
  });

  let latestStripeDate = dateFormat(stripesData.slice(-1)[0]['date']);

  const stripesContainer = document.getElementById('stripes');
  new Stripes({
    target: stripesContainer,
    props: {
      data: stripesData,
      width: stripesContainer.offsetWidth,
      title: 'California&rsquo;s drought stripes',
      footnote: `Stripes are colored based on the &ldquo;one-year standardized precipitation evapotranspiration index,&rdquo; or the one-year standard deviation of water balance from the average. Each stripe represents five days. Data as of ${latestStripeDate}.`,
      sourceline:
        'Katherine Hegewisch and John Abatzoglou, <a href="https://climatetoolbox.org/tool/historical-drought-stripes">Historical Drought Stripes, Climate Toolbox</a>',
    },
  });

  // PRECIPITATION

  const precipData = loadJsonScript('precip-data');

  const heatMapContainer = document.getElementById('precip-bands');

  renderSvelteComponent(HeatMap, heatMapContainer, {
    data: precipData,
    width: heatMapContainer.offsetWidth,
    region: 'Statewide',
    legendTitle: 'Daily precipitation, in inches',
  });

  // US drought monitor maps
  const comparisonDroughtMonitorMetadata = loadJsonScript(
    'comparison-drought-monitor-metadata'
  );

  comparisonDroughtMonitorMetadata.map((d) => {
    d.date = dateParser(d.date);
    return d;
  });

  const comparisonMap = loadJsonScript('comparison-drought-map');
  new UsMap({
    target: document.getElementById('comparison-us-map'),
    props: {
      title: 'U.S. drought conditions',
      data: comparisonMap,
      date: comparisonDroughtMonitorMetadata[0].date,
      showTitle: true,
      showDate: true,
      includeKey: true,
      showDate: true,
      showCities: true,
    },
  });

  const currentDroughtMonitorMetadata = loadJsonScript(
    'current-drought-monitor-metadata'
  );

  currentDroughtMonitorMetadata.map((d) => {
    d.date = dateParser(d.date);
    return d;
  });

  const currentDrought = loadJsonScript('current-drought-map');
  new UsMap({
    target: document.getElementById('current-us-map'),
    props: {
      title: 'Current U.S. drought conditions',
      data: currentDrought,
      date: currentDroughtMonitorMetadata[0].date,
      showTitle: false,
      showDate: true,
      includeKey: false,
      showDate: true,
      showCities: false,
    },
  });

  (function () {
    'use strict';

    let initialized = false;

    window.addEventListener('message', (e) => {
      const conditions = [
        () => !initialized,
        () => e.data,
        () => e.data.chartId,
        () => e.data.chartId === 'IiSvF',
        () => e.data.type,
        () => e.data.type === 'vis.rendered',
      ];

      if (conditions.some((v) => !v())) return;

      const vis = document.querySelector(
        '.urban-water-use-map .datawrapper-script-embed > *'
      );

      if (vis) {
        initialized = true;

        // const toggleButton = document.getElementById('dw-toggle');
        const rgpcdButton = document.getElementById('rgpcd-toggle');
        const supplyButton = document.getElementById('supply-toggle');

        supplyButton.addEventListener('click', async (e) => {
          vis.patch('metadata.axes.values', '');
          vis.patch('metadata.axes.area', 'total_water_production');
          vis.patch('metadata.visualize.legends', {
            size: {
              title: 'Total potable water supply (gallons)',
              enabled: true,
              titleEnabled: true,
            },
            color: { enabled: false },
          });
          if (supplyButton.className != 'toggle active') {
            supplyButton.classList.toggle('active');
            rgpcdButton.classList.toggle('active');
          }
        });
        rgpcdButton.addEventListener('click', async (e) => {
          vis.patch('metadata.axes.values', 'r_gpcd');
          vis.patch('metadata.axes.area', '');
          vis.patch('metadata.visualize.legends', {
            size: { enabled: false },
            color: {
              enabled: true,
              title: 'Residential water use (gallons per person per day)',
            },
          });
          if (rgpcdButton.className != 'toggle active') {
            supplyButton.classList.toggle('active');
            rgpcdButton.classList.toggle('active');
          }
        });
      }
    });
  })(); // end datawrapper function
}

main().catch(console.error);

document.addEventListener('DOMContentLoaded', () => {
  // Kickstart image lazy loading
  lazyload();
  // Kickstart ads
  const adLoader = new AdLoader({ adSection: window.AD_SECTION });
  adLoader.init();
});

lazyLoader.init({ rootMargin: '500px 0px' });
